import { useState, useCallback } from 'react';
import { message } from 'antd';
import { pwh508Api, ApproveResultItem } from '../../../services/pwh508Api';
import type { ItemState } from './usePWH508Queue';

export function usePWH508Approval(items: ItemState[], resetItems: () => void) {
  const [executing, setExecuting] = useState(false);
  const [lastResults, setLastResults] = useState<ApproveResultItem[]>([]);

  /**
   * Execute batch approve for all selected items. Always sends
   * confirmed:true — the confirmation itself already happened in the
   * Modal.confirm the caller shows before invoking this (BR-V02).
   * The backend response is a flat per-document results[] list (no
   * top-level success/failed counters), so those are derived here.
   */
  const handleExecute = useCallback(async (): Promise<boolean> => {
    const selectedNos = items.filter((s) => s.selected).map((s) => s.item.fh110_01);

    if (selectedNos.length === 0) {
      message.warning('請先勾選要核准的請款單');
      return false;
    }

    setExecuting(true);
    try {
      const res = await pwh508Api.approve(selectedNos);
      const results = res.results || [];
      setLastResults(results);

      const successCount = results.filter((r) => r.success).length;
      const failedCount = results.length - successCount;

      if (failedCount === 0) {
        message.success(`核准完成，成功 ${successCount} 筆`);
      } else {
        message.warning(`成功 ${successCount} 筆，失敗 ${failedCount} 筆`);
        results
          .filter((r) => !r.success)
          .forEach((r) => message.error(`${r.doc_key}: ${r.message || '核准失敗'}`));
      }

      // BR-F15: clear the queue and force a re-query after approval
      resetItems();
      return true;
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '核准執行失敗');
      return false;
    } finally {
      setExecuting(false);
    }
  }, [items, resetItems]);

  return { executing, lastResults, handleExecute };
}
